import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from '@core/services/auth-service/auth-service.service';
import { environment } from '@environment/environment';
import { BehaviorSubject, Subject } from 'rxjs';
import { TabIdentifier } from '../tab-identifier/tab-identifier.service';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private socket: WebSocket | undefined;
  private isReady = new BehaviorSubject<boolean>(false);
  private listeners: { [key: string]: ((message: any) => void)[] } = {};
  private tryAgainTime = 1;
  private dontTryAgain = !this.authService.getLoggedUser()?.token;
  private tryingToConnect = false;
  private socketErrorCount = 0;
  private lastCloseCode: number | undefined;
  private deferred: Subject<void> | undefined;
  private pingInterval: any;
  private tabIdentifier = '';

  initializeService() {
    if (!this.tryingToConnect) {
      this.initPing();
      window.onbeforeunload = this.closeConnection.bind(this);
    }
  }

  constructor(
    private authService: AuthService,
    private tabIdentifierService: TabIdentifier,
  ) {
    this.initializeService();
    this.authService.loggedUser.subscribe((x) => {
      console.log('INITIALIZING....');
      this.initializeService();
    });
    this.authService.finishedLogout.subscribe((x) => {
      console.log('CLOSING ALL');
      this.closeConnection();
    });
  }

  private onOpen(): void {
    this.socketErrorCount = 0;
    this.isReady.next(true);
    this.tryAgainTime = 1;
    this.deferred?.next();
  }

  private onClose(event: CloseEvent): void {
    this.lastCloseCode = event.code;
    this.isReady.next(false);
    this.keepTryingToConnect();
  }

  private onError(event: Event): void {
    this.socketErrorCount += 1;
    if (this.socketErrorCount === 10) {
      setTimeout(() => {
        throw new Error(
          `Repeated errors disconnected the WebSocket: ${this.lastCloseCode}`,
        );
      }, 120000); // 2 minutes delay
    }
    this.isReady.next(false);
    this.keepTryingToConnect();
  }

  private onMessage(event: MessageEvent): void {
    const dataObject = JSON.parse(event.data);
    const { type, message } = dataObject;
    if (
      message !== null &&
      message !== undefined &&
      Object.prototype.hasOwnProperty.call(message, 'tab_identifier')
    ) {
      if (message.tab_identifier === this.tabIdentifierService.tabIdentifier) {
        // It is our own message
        return;
      }
    }

    if (this.listeners[type]) {
      this.listeners[type].forEach((listener) => {
        try {
          listener(message);
        } catch (e) {
          console.warn(
            `An error occurred while receiving message (${message}) of type (${type})`,
          );
        }
      });
    }
  }

  private connect(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return; // Already connected
    }
    if (!this.authService.getLoggedUser()?.client_id) {
      return; // Esperando o client_id chegar, esse caso acontece quando o usuário só pode acessar a página de discussão externa
    }
    const url = `${environment.websocketEndpoint}/ws?token=${this.authService.getLoggedUser()?.token}&client_id=${this.authService.getLoggedUser()?.client_id}`;

    try {
      this.socket = new WebSocket(url);
      this.socket.onopen = this.onOpen.bind(this);
      this.socket.onmessage = this.onMessage.bind(this);
      this.socket.onerror = this.onError.bind(this);
      this.socket.onclose = this.onClose.bind(this);
    } catch (e) {
      throw new Error('An error occurred while connecting to the WebSocket');
    }
  }

  private keepTryingToConnect(): void {
    this.dontTryAgain = !this.authService.getLoggedUser()?.token;
    if (this.isReady.value || this.dontTryAgain) {
      this.tryingToConnect = false;
      return;
    }

    if (this.tryingToConnect) {
      return;
    }

    this.tryingToConnect = true;
    setTimeout(() => {
      this.tryingToConnect = false;
      if (!this.isReady.value) {
        this.connect();
        this.keepTryingToConnect();
      } else {
        this.tryAgainTime = 1;
      }
    }, this.tryAgainTime);

    this.tryAgainTime += 500;
    this.tryAgainTime *= 2;
    if (this.tryAgainTime > 1000000) {
      this.tryAgainTime = 1000000;
    }
  }

  listen(type: string, listenerFunction: (message: any) => void): any {
    this.deferred = new Subject<void>();

    if (!this.listeners[type]) {
      this.listeners[type] = [];
    }

    if (!this.listeners[type].includes(listenerFunction)) {
      this.listeners[type].push(listenerFunction);
    } else {
      throw new Error(`Same listener added more than once for type: ${type}`);
    }

    if (!this.isReady.value) {
      this.keepTryingToConnect();
    } else {
      this.deferred.next();
    }

    return {
      type: type,
      listenerFunction: listenerFunction,
    };
  }

  stopListening(listenerData: any) {
    if (listenerData === null || listenerData === undefined) return;
    try {
      this.listeners[listenerData.type] = this.listeners[
        listenerData.type
      ].filter((f) => f !== listenerData.listenerFunction);
    } catch (err) {
      //It might have already log out so the key doesn`t exist anymore
    }
  }

  removeListener(type: string): void {
    if (this.listeners[type]) {
      delete this.listeners[type];
    }
  }

  hasListener(type: string): boolean {
    return !!this.listeners[type];
  }

  private closeConnection(): void {
    if (this.socket) {
      try {
        this.socket.close();
      } catch (err) {
        // Ignore any errors while closing the socket.
      }
      this.dontTryAgain = true;

      this.listeners = {};
    }
  }

  // Enviar a cada 9 minutos uma mensagem para o api gateway apenas para manter a conexão aberta
  // O idle timeout é de 10 minutos. Após 2 horas a conexão fecha automaticamente e é reaberta.
  private initPing(): void {
    this.pingInterval = setInterval(() => {
      if (!this.isReady.value || !this.socket) {
        return;
      }
      const data = JSON.stringify({ action: 'ping' });
      try {
        this.socket.send(data);
      } catch (err) {
        this.isReady.next(false);
        this.keepTryingToConnect();
      }
    }, 540000); // Every 9 minutes
  }

  ngOnDestroy(): void {
    clearInterval(this.pingInterval);
    this.closeConnection();
  }
}
